// React Hooks
import { useContext, useState, MouseEvent } from "react";
// Data
import { t } from "i18next";
// MUI JSX
import {
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Autocomplete,
} from "@mui/material";
// Custom JSX
import CircleIcon from "@mui/icons-material/Circle";
import { UserClaims, AuthContext } from "core/contexts/auth/auth";
import { IHeadCell } from "core/components/global/tables/datagrid/dataGridHeader/datagridHeader";
import DataGridRow from "./dataGridRow";

interface DarkDatagridRows {
    headCells: IHeadCell[];
    visibleRows: any, 
    visibleKeys: string[],
    page: number,
    rowsPerPage: number,
    totalRows: number,
    highlightedRowId: any, setHighlightedRowId: any, 
    selected, setSelected: any, 
    onRowClick: any,
    maxWidths: any,
    handleAssignedTo?: (email: string, id: string) => void;
    users?: UserClaims[];
    statusMapping?: { [key: string]: string };
    handleSetStatus?: (status: string, rowId: string) => void;
    onActionClick?:(rowId:string,type:string) => void;
}

interface KeyValue {
    [key: string]: string; // Change the value type to string
}

export interface UserClaim {
    given_name: string;
    family_name: string;
    email: string;
    firstLetter: string;
}

interface AssignedToDropDownProps {
    users: UserClaims[] | undefined;
    handleAssignedTo: any;
    sortField: string;
    rowId: string | number;
    defaultValue: string;
}

interface StatusChangeDropDownProps {
    statusMapping: any;
    statuses: string[];
    handleSetStatus: any;
    rowId: string;
    defaultValue: string;
}

export function StatusChangeDropDown({ statusMapping, statuses, handleSetStatus, rowId, defaultValue }: StatusChangeDropDownProps) {
    const [selectedStatus, setSelectedStatus] = useState<KeyValue>();
    const [userInputValue, setUserInputValue] = useState(defaultValue);
    
    if (!statuses) {
        return <></>;
    }
    
    /* 
        setSelectedStatus is a dict with rowId as a key instead of a string
        to prevent the MUI pagination from displaying the selected value
        on the same row number of every page. This was discovered during
        testing of the standaed MUI Autocomplere component examples so
        modifications were needed.
    */
    const defaultObject = Object({ [rowId]: defaultValue })
    
    function handleSetSelectedStatus(status) {
        // If x was clicked, change the status to open 
        status = status ? status : t("STATUS.OPEN");
        const temp = { ...selectedStatus, [rowId]: status };
        setSelectedStatus(temp)
        handleSetStatus(status, rowId)  
    }

    return (
        <div className="d-flex flex-row align-items-center">
            <CircleIcon
                style={{
                    color: statusMapping[(selectedStatus?.[rowId] || defaultValue)],
                    verticalAlign: "middle",
                    fontSize: 12,
                    marginRight: 5,
                }}
            />
            <Autocomplete
                sx={{ width: 200, fontSize: '12px' }}
                disablePortal
                options={statuses}
                value={!selectedStatus ? defaultObject[rowId] : selectedStatus[rowId]}
                onChange={(_, status: string) => {
                    handleSetSelectedStatus(status);
                }}
                inputValue={userInputValue}
                onInputChange={(_, newuserInputValue) => {
                    setUserInputValue(newuserInputValue);
                }}
                renderInput={(params) => {
                    return <TextField 
                        {...params} sx={{"& fieldset": { border: 'none', "color": 'blue'}}}
                    />;
                }}
            />
        </div>
    );
}

export function AssignedToDropDown({ users, handleAssignedTo, sortField, rowId, defaultValue }: AssignedToDropDownProps) {
    const [selectedUser, setselectedUser] = useState<KeyValue>();
    const [userInputValue, setUserInputValue] = useState(defaultValue);

    if (!users) {
        return <></>;
    }
    
    /* 
        selectedUser is a dict with rowId as a key instead of a string
        to prevent the MUI pagination from displaying the selected value
        on the same row number of every page. This was discovered during
        testing of the standaed MUI Autocomplere component examples so
        modifications were needed.
    */
    const defaultObject = Object({ [rowId]: defaultValue })

    function handleSetselectedUser(email) {
        // If x was selected the email should be blank
        email = email ? email : "";
        // if users were obtained successfully from the APi
        if (users) {
            const user = users.filter((user) => user.email === email)[0]
            if (user) {
                const temp = { ...selectedUser, [rowId]: user.email };
                setselectedUser(temp)
                handleAssignedTo(email, rowId)
            } else {
                const temp = { ...selectedUser, [rowId]: "" };
                setselectedUser(temp)
                handleAssignedTo("", rowId)
            }
        }
    }

    return (
        <div className="d-flex flex-row align-items-center">
            <Autocomplete
                disablePortal
                sx={{ width: 200, fontSize: '12px' }}
                options={users
                    .sort((a, b) => -b.given_name[0].localeCompare(a.given_name[0]))
                    .map((user) => user.email)
                }
                value={!selectedUser ? defaultObject[rowId] : selectedUser[rowId]}
                groupBy={(user) => {
                    return(user[0])
                }}
                onChange={(_, newPet: string) => {
                    handleSetselectedUser(newPet);
                }}
                inputValue={userInputValue}
                onInputChange={(_, newuserInputValue) => {
                    setUserInputValue(newuserInputValue);
                }}
                renderInput={(params) => {
                    return <TextField 
                        {...params} 
                        sx={{"& fieldset": { border: 'none' }}}
                    />;
                }}
            />
        </div>
    );
}

export default function DarkDatagridRows({
    headCells,
    visibleRows, visibleKeys,
    page, rowsPerPage, totalRows,
    highlightedRowId, setHighlightedRowId, 
    selected, setSelected, 
    onRowClick,
    maxWidths,
    handleAssignedTo,
    users,
    statusMapping,
    handleSetStatus,
    onActionClick
}: DarkDatagridRows) {
    const { afUser } = useContext(AuthContext);

    const getHighlightedRowData = (rowId) => {
        const highlightedRowData = visibleRows.find(
          (visibleRow) => visibleRow.id === rowId
        );
        return highlightedRowData;
    };

    const getMultiSelectedRowIds = (newSelected: readonly string[]) => {
        const checkedId = visibleRows.find(
            (visibleRow) => visibleRow.findingId === newSelected[newSelected.length - 1]
        )?.findingId;
        return checkedId ? [checkedId] : [];
    };
    
    const handleCheckBoxClick = (
        event: MouseEvent<unknown>,
        name: string | number
      ) => {
        event.stopPropagation();
        const isCheckBoxSelected =
          (event.target as HTMLButtonElement).tagName === "INPUT";
    
        let newSelected: readonly string[] = [];
        
        if (isCheckBoxSelected) {
          const selectedIndex = selected.indexOf(name as string);
    
          if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name as string);
          } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
          } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
          } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
              selected.slice(0, selectedIndex),
              selected.slice(selectedIndex + 1)
            );
          }
          setSelected(newSelected);
        } else if (setHighlightedRowId) setHighlightedRowId(name as string);
    
        const checkedRowsIds = getMultiSelectedRowIds(
          isCheckBoxSelected ? newSelected : selected
        );

        console.log(`onRowClick checkedRowsIds: ${checkedRowsIds}`)
    
        if (onRowClick) {
          onRowClick({
            checkedRows: checkedRowsIds,
            highlightedRow: !isCheckBoxSelected && getHighlightedRowData(name),
          });
        }
    };
    
    const handleIdClick = (id, type) => {
        console.log(`handleIdClick called with id: ${id}`);
        if (onRowClick) {
            console.log('onRowClick exists, calling it');

            console.log(`onRowClick id: ${id}`)
            onRowClick(id);
        } else {
            console.log('onRowClick is undefined or null');
        }
    }

    const isSelected = (id: string) => selected.indexOf(id) !== -1;
    const isCheckBoxChecked = (rowId: string) => {
        return !!selected.find((id) => id === rowId);
    };

    return(
        <>
            {visibleRows.length === 0 ? (
                <TableBody>
                    <TableRow key={0}>
                        <TableCell colSpan={12} align="center">
                            {t("DATAGRID.EMPTY_ROWS")}
                        </TableCell>
                    </TableRow>
                </TableBody>
            ) : (
                <TableBody>
                    {visibleRows.map((row, index) => {
                        return (
                            <DataGridRow
                                key={`data-grid-row-${row.id}-${index}`}
                                t={t} row={row} headers={headCells} index={index} isSelected={isSelected}
                                highlightedRowId={highlightedRowId} visibleKeys={visibleKeys}
                                handleCheckBoxClick={handleCheckBoxClick} isCheckBoxChecked={isCheckBoxChecked} 
                                handleIdClick={handleIdClick} maxWidths={maxWidths}
                                statusMapping={statusMapping} user={afUser} handleSetStatus={handleSetStatus} 
                                users={users} handleAssignedTo={handleAssignedTo} onActionClick={onActionClick}
                            />
                        );
                    })}
                </TableBody>
            )}
        </>
    )
}